import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import find from 'lodash/fp/find';

import { type Invitation, isProblem } from '../services/partnerNetwork.types';
import {
    useFetchAccountProfileQuery,
    useFetchInvitationQuery,
    useFetchPartnerConnectionTypesQuery,
    useUpdateInvitationMutation,
} from '../services/partnerNetworkApi';
import { DEFAULT_ROUTE } from '../routes/routes';
import { partnerSelected, showServiceInfoDialog } from '../layout/appSlice';
import { useAppDispatch } from '../configuration/setup/hooks';
import PendingInvitationState from '../features/partnerInvitation/PendingInvitationState';
import LoadingInvitationState from '../features/partnerInvitation/LoadingInvitationState';
import InvalidInvitationState from '../features/partnerInvitation/InvalidInvitationState';
import { getTrackingAttributes, TRACKING_CATEGORIES } from '../configuration/setup/googleAnalytics';

export const INVITATION_PARTNER_QUERY = 'partner';
export const INVITATION_ACCESS_TOKEN_QUERY = 'accessToken';

const PartnerInvitation = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [rejectionReason, setRejectionReason] = useState<string>();

    const selectPartner = (selectedPartnerId: string | undefined) => dispatch(partnerSelected(selectedPartnerId));

    const [updateInvitation] = useUpdateInvitationMutation();

    const location = useLocation();
    const queryString = location.search;

    const urlParams = new URLSearchParams(queryString);
    const partnerId = urlParams.get(INVITATION_PARTNER_QUERY) || undefined;
    const id = urlParams.get(INVITATION_ACCESS_TOKEN_QUERY) || undefined;

    const { data: invitation, isLoading: isLoadingInvitation } = useFetchInvitationQuery(
        { id, partnerId },
        { skip: !(id && partnerId) }
    );

    const { data: partnerConnectionTypes, isLoading: isLoadingPartnerConnectionTypes } =
        useFetchPartnerConnectionTypesQuery();

    const { data: accountProfile, isLoading: isLoadingAccountProfile } = useFetchAccountProfileQuery(partnerId, {
        skip: !partnerId,
    });

    const partnerConnectionType = find(
        partnerConnectionType => partnerConnectionType.connectionType === invitation?.connectionType,
        partnerConnectionTypes?.items
    );

    useEffect(() => {
        selectPartner(undefined);
    }, []);

    const handleAccept = async () => {
        if (invitation) {
            const acceptedInvitation: Invitation = {
                ...invitation,
                status: 'accepted',
            };
            const result = await updateInvitation(acceptedInvitation);
            if (result.error) {
                setRejectionReasonBasedOnError(result.error);
            } else {
                selectPartner(acceptedInvitation.partnerId);

                // Hide welcome screen right after accepting the partner invitation
                dispatch(showServiceInfoDialog(false));

                if (invitation?.redirectUri) {
                    window.location.replace(invitation.redirectUri);
                } else {
                    navigate(DEFAULT_ROUTE);
                }
            }
        } else {
            navigate(DEFAULT_ROUTE);
        }
    };

    const handleLeave = () => navigate(DEFAULT_ROUTE);
    // biome-ignore lint/suspicious/noExplicitAny: necessary to check if there is an error message
    const setRejectionReasonBasedOnError = (error: any): void => {
        if (error?.data) {
            try {
                const problem: unknown = JSON.parse(error.data);
                if (isProblem(problem)) {
                    setRejectionReason(error.data.detail);
                }
            } catch (e) {
                console.error('Failed to parse error data', e);
            }
        }
    };

    if (isLoadingInvitation || isLoadingAccountProfile || isLoadingPartnerConnectionTypes) {
        return <LoadingInvitationState />;
    }

    if (invitation && invitation.status === 'pending' && !rejectionReason) {
        return (
            <ApplicationLayout.Body>
                <PendingInvitationState
                    {...getTrackingAttributes({
                        trigger: 'click',
                        category: TRACKING_CATEGORIES.INVITATION,
                        action: 'Accept partner invitation clicked',
                        label: 'Accept partner invitation clicked',
                    })}
                    invitation={invitation}
                    partnerConnectionType={partnerConnectionType}
                    accountProfile={accountProfile}
                    onClick={handleAccept}
                />
            </ApplicationLayout.Body>
        );
    }

    return (
        <ApplicationLayout.Body>
            <InvalidInvitationState rejectionReason={rejectionReason} onClick={handleLeave} />
        </ApplicationLayout.Body>
    );
};

export default PartnerInvitation;
